import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ListItem, ListItemButton, ListItemIcon, ListItemText, type SxProps, type Theme } from '@mui/material';

interface MenuListItemProps {
	open: boolean;
	icon?: React.ReactElement;
	rightIcon?: React.ReactElement;
	primaryText?: string;
	secondaryText?: string;
	onClick?: (event?: React.MouseEvent<HTMLElement>) => void;
	to?: string;
	showText?: boolean;
	additionalSx?: object;
	selected?: boolean;
	ariaLabel?: string;
	primaryTextSx?: SxProps<Theme>;
}

const MenuListItem: React.FC<MenuListItemProps> = ({
	open,
	icon,
	rightIcon,
	primaryText,
	secondaryText,
	onClick,
	to,
	showText = true,
	additionalSx = {},
	selected,
	ariaLabel,
	primaryTextSx,
}) => {
	const linkProps = to ? { component: RouterLink, to } : {};

	return (
		<ListItem
			disablePadding
			sx={{
				display: 'block',
				...additionalSx,
			}}
		>
			<ListItemButton
				onClick={onClick}
				aria-label={ariaLabel}
				{...linkProps}
				selected={selected}
				sx={[
					open
						? {
								minHeight: 36,
								py: 0.5,
								justifyContent: 'initial',
								px: 2,
							}
						: {
								minHeight: 50,
								py: 0.9,
								justifyContent: 'center',
								px: 0.5,
							},
					{
						display: 'flex',
						alignItems: 'center',
					},
					{
						borderRadius: 3,
						color: (theme) => theme.palette.navigation.item,
						'&:hover': {
							backgroundColor: (theme) => theme.palette.navigation.itemHover,
						},
					},
					{
						'&.Mui-selected': {
							backgroundColor: (theme) => theme.palette.navigation.selectedBackground,
							color: (theme) => theme.palette.navigation.selectedText,
							'&:hover': {
								backgroundColor: (theme) => theme.palette.navigation.selectedBackground,
							},
						},
					},
				]}
			>
				{icon && (
					<ListItemIcon
						sx={[
							{
								minWidth: 0,
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
							},
							open
								? {
										mr: 1.5,
									}
								: {
										mr: 0,
									},
							selected
								? {
										color: 'navigation.selectedIcon',
									}
								: {
										color: 'text.secondary',
									},
						]}
					>
						{icon}
					</ListItemIcon>
				)}
				{open && showText && (
					<ListItemText
						primary={primaryText}
						secondary={secondaryText}
						primaryTypographyProps={{
							fontWeight: selected ? 600 : 500,
							fontSize: '14px',
							sx: primaryTextSx,
						}}
						sx={{
							opacity: 1,
						}}
					/>
				)}
				{rightIcon && (
					<ListItemIcon
						sx={{
							minWidth: 0,
							justifyContent: 'center',
							ml: 'auto',
						}}
					>
						{rightIcon}
					</ListItemIcon>
				)}
			</ListItemButton>
		</ListItem>
	);
};

export default MenuListItem;
